import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { type User } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '@/firebase/config';
// @ts-ignore — AuthContext es JSX
import { useAuth } from '@/contexts/AuthContext';
import { clearBordonaSessionCache } from '@/bordona/lib/clearBordonaSessionCache';
import type { StudentProfile } from '@/bordona/lib/taskEngine';
import {
  PLAY_STUDENT_PROFILE_STORAGE,
  STUDENT_PROFILES_COLLECTION,
} from '../lib/edusicoFirestore';

export type PlayAccountRole = 'teacher' | 'student' | null;

interface PlaySessionContextValue {
  user: User | null;
  isAuthLoading: boolean;
  isAuthenticated: boolean;
  role: PlayAccountRole;
  isTeacher: boolean;
  studentProfile: StudentProfile | null;
  /** Nombre para saludar en la UI */
  userName: string;
  /** Listo para usar la app (logueado + perfil estudiante o cuenta profesor) */
  hasSession: boolean;
  /** Alias histórico de hasSession */
  hasName: boolean;
  accessProfile: Record<string, unknown> | null;
  authError: string | null;
  signInWithEdusico: () => void;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  saveStudentProfile: (firstName: string, lastName: string, rut?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  setUserName: (name: string) => void;
}

const PlaySessionContext = createContext<PlaySessionContextValue | null>(null);

function loadStoredStudentProfile(): StudentProfile | null {
  try {
    const raw = localStorage.getItem(PLAY_STUDENT_PROFILE_STORAGE);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStoredStudentProfile(profile: StudentProfile) {
  localStorage.setItem(PLAY_STUDENT_PROFILE_STORAGE, JSON.stringify(profile));
}

async function loadFirestoreStudentProfile(uid: string): Promise<StudentProfile | null> {
  try {
    const snap = await getDoc(doc(db, STUDENT_PROFILES_COLLECTION, uid));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      uid,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      rut: data.rut,
      accountType: 'estudiante',
      createdAt: typeof data.createdAt === 'number' ? data.createdAt : Date.now(),
    };
  } catch {
    return null;
  }
}

export function PlaySessionProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth() as {
    user: User | null;
    loading: boolean;
    accessProfile: Record<string, unknown> | null;
    profesorData: Record<string, unknown> | null;
    accountType?: string;
    error: string | null;
    login: (email: string, password: string) => Promise<User>;
    loginWithGoogle: () => Promise<User>;
    logout: () => Promise<void>;
  };

  const [studentProfile, setStudentProfile] = useState<StudentProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [studentCheckDone, setStudentCheckDone] = useState(false);
  const [studentProfileExists, setStudentProfileExists] = useState(false);
  const [localDisplayOverride, setLocalDisplayOverride] = useState('');

  const isTeacher = useMemo(() => {
    if (!auth.user) return false;
    const type = auth.accountType || auth.accessProfile?.accountType;
    if (type === 'estudiante') return false;
    if (studentProfileExists) return false;
    if (auth.profesorData && !auth.profesorData._perfilTemporal) return true;
    return type === 'profesor';
  }, [auth.user, auth.profesorData, auth.accountType, auth.accessProfile, studentProfileExists]);

  useEffect(() => {
    if (!auth.user?.uid) {
      setStudentProfileExists(false);
      setStudentCheckDone(false);
      return;
    }
    if (isTeacher) {
      setStudentProfileExists(false);
      setStudentCheckDone(true);
      return;
    }

    let cancelled = false;
    setStudentCheckDone(false);
    getDoc(doc(db, STUDENT_PROFILES_COLLECTION, auth.user.uid))
      .then((snap) => {
        if (!cancelled) {
          setStudentProfileExists(snap.exists());
          setStudentCheckDone(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStudentProfileExists(false);
          setStudentCheckDone(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [auth.user?.uid, isTeacher]);

  useEffect(() => {
    const user = auth.user;
    if (!user) {
      setStudentProfile(null);
      return;
    }

    if (isTeacher) {
      localStorage.removeItem(PLAY_STUDENT_PROFILE_STORAGE);
      setStudentProfile(null);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);

    (async () => {
      const fromFs = await loadFirestoreStudentProfile(user.uid);
      if (cancelled) return;

      if (fromFs) {
        saveStoredStudentProfile(fromFs);
        setStudentProfile(fromFs);
        setProfileLoading(false);
        return;
      }

      const stored = loadStoredStudentProfile();
      if (stored && stored.uid === user.uid) {
        setStudentProfile(stored);
      } else {
        setStudentProfile(null);
      }
      setProfileLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [auth.user, isTeacher]);

  const role = useMemo((): PlayAccountRole => {
    if (!auth.user) return null;
    if (isTeacher) return 'teacher';
    if (!studentCheckDone) return null;
    if (studentProfileExists || studentProfile) return 'student';
    return 'student';
  }, [auth.user, isTeacher, studentCheckDone, studentProfileExists, studentProfile]);

  const userName = useMemo(() => {
    if (localDisplayOverride.trim()) return localDisplayOverride.trim();
    if (studentProfile) {
      return studentProfile.firstName.trim() || 'Explorador';
    }
    if (isTeacher) {
      const pd = auth.profesorData as { nombre?: string; apellido?: string; nombreCompleto?: string } | null;
      const fromProf =
        pd?.nombreCompleto ||
        [pd?.nombre, pd?.apellido].filter(Boolean).join(' ').trim();
      return (
        fromProf ||
        (auth.accessProfile?.displayName as string) ||
        auth.user?.displayName ||
        'Profesor'
      );
    }
    return '';
  }, [localDisplayOverride, studentProfile, isTeacher, auth.profesorData, auth.accessProfile, auth.user]);

  const hasSession = useMemo(() => {
    if (!auth.user || auth.loading) return false;
    if (isTeacher) return true;
    if (profileLoading || !studentCheckDone) return false;
    return !!studentProfile;
  }, [auth.user, auth.loading, isTeacher, profileLoading, studentCheckDone, studentProfile]);

  const signInWithEdusico = useCallback(() => {
    const returnTo = encodeURIComponent(window.location.href);
    window.location.href = `https://edusico.cl/login?returnTo=${returnTo}`;
  }, []);

  const saveStudentProfile = useCallback(
    async (firstName: string, lastName: string, rut?: string): Promise<boolean> => {
      const user = auth.user;
      if (!user) return false;

      const trimmedRut = rut?.trim();
      const newProfile: StudentProfile = {
        uid: user.uid,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(trimmedRut ? { rut: trimmedRut } : {}),
        accountType: 'estudiante',
        createdAt: Date.now(),
      };

      const firestorePayload: Record<string, unknown> = {
        uid: newProfile.uid,
        firstName: newProfile.firstName,
        lastName: newProfile.lastName,
        accountType: newProfile.accountType,
        createdAt: newProfile.createdAt,
        email: user.email,
        displayName: `${newProfile.firstName} ${newProfile.lastName}`,
        updatedAt: Date.now(),
      };
      if (trimmedRut) firestorePayload.rut = trimmedRut;

      try {
        await setDoc(
          doc(db, STUDENT_PROFILES_COLLECTION, user.uid),
          firestorePayload,
          { merge: true }
        );
        saveStoredStudentProfile(newProfile);
        setStudentProfile(newProfile);
        setStudentProfileExists(true);
        setLocalDisplayOverride('');
        return true;
      } catch (err) {
        console.error('Error guardando perfil Play:', err);
        return false;
      }
    },
    [auth.user]
  );

  const logout = useCallback(async () => {
    await auth.logout();
    clearBordonaSessionCache();
    try {
      localStorage.removeItem(PLAY_STUDENT_PROFILE_STORAGE);
      localStorage.removeItem('play-user-name');
    } catch {
      /* ignore */
    }
    setStudentProfile(null);
    setLocalDisplayOverride('');
  }, [auth]);

  const value = useMemo(
    (): PlaySessionContextValue => ({
      user: auth.user,
      isAuthLoading: auth.loading || (!!auth.user && !isTeacher && (profileLoading || !studentCheckDone)),
      isAuthenticated: !!auth.user,
      role,
      isTeacher,
      studentProfile,
      userName,
      hasSession,
      hasName: hasSession,
      accessProfile: auth.accessProfile,
      authError: auth.error,
      signInWithEdusico,
      login: async (email, password) => {
        await auth.login(email, password);
      },
      loginWithGoogle: async () => {
        await auth.loginWithGoogle();
      },
      saveStudentProfile,
      logout,
      setUserName: setLocalDisplayOverride,
    }),
    [
      auth,
      role,
      isTeacher,
      studentProfile,
      userName,
      hasSession,
      profileLoading,
      studentCheckDone,
      signInWithEdusico,
      saveStudentProfile,
      logout,
    ]
  );

  return (
    <PlaySessionContext.Provider value={value}>{children}</PlaySessionContext.Provider>
  );
}

export function usePlaySession(): PlaySessionContextValue {
  const ctx = useContext(PlaySessionContext);
  if (!ctx) throw new Error('usePlaySession must be used within PlaySessionProvider');
  return ctx;
}

/** Compatibilidad con código que usaba PlayProfileContext */
export function usePlayProfile() {
  const s = usePlaySession();
  return {
    userName: s.userName,
    setUserName: s.setUserName,
    hasName: s.hasName,
  };
}
